import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { antGrey } from '../styles/globalStyles';
import { AppStoreType, PhasedRelease } from '../../types';
import {
    phasedReleasePausedDisplayTextForAppStoreType,
    phasedReleaseDisplayTextForAppStoreType,
} from '../../helpers/displayHelpers';
import { formatResumedAtString } from '../monitoring/PhasedReleaseCard';

interface PhasedReleaseDayTextProps {
    appId: string;
    appStoreType: AppStoreType;
    phasedReleaseDay: number;
    phasedRelease: PhasedRelease;
    isPhasedReleasePaused: boolean;
    androidStagedRolloutNotStarted: boolean;
    textStyle?: React.CSSProperties;
    maxWidth?: number;
    daysToPercent: number[];
    currentPercent?: number;
}

const PhasedReleaseDayText: React.FC<PhasedReleaseDayTextProps> = (props: PhasedReleaseDayTextProps) => {
    const dayCount = props.daysToPercent?.length ?? 7;

    const phasedDisplayText = phasedReleasePausedDisplayTextForAppStoreType(props.appStoreType);
    const phasedReleaseText = phasedReleaseDisplayTextForAppStoreType(props.appStoreType);

    const baseTextStyle: React.CSSProperties = {
        fontSize: 14,
        color: antGrey.grey7,
        textAlign: 'right',
    };
    const textStyles: React.CSSProperties =
        props.textStyle ?
            {
                ...baseTextStyle,
                ...props.textStyle,
            }
        :   { ...baseTextStyle };

    // When the rollout is halted at 100%, the rollout is effectively over: the "Day N of an
    // M-day rollout" framing (and the blackout copy) is meaningless — the saved rollout day is
    // derived from the percentage→schedule-day mapping, so a release promoted straight to 100%
    // reports the schedule's final day regardless of elapsed time. Show no day text in this case.
    const isHaltedAtFullRollout =
        props.isPhasedReleasePaused && (props.phasedRelease?.phasedReleasePercent ?? 0) >= 100;
    if (isHaltedAtFullRollout) {
        return null;
    }

    // The blackout pause is performed by Runway (isRunwayPaused=true). A user manually halting
    // the rollout leaves isRunwayPaused=false, even if we happen to be inside a blackout window.
    // Don't let the blackout copy mask the real reason (manual halt) in that case.
    const isManuallyHalted = props.isPhasedReleasePaused && !props.phasedRelease?.isRunwayPaused;
    const shouldShowBlackoutText = !!props.phasedRelease?.isOnBlackoutPeriod && !isManuallyHalted;

    const phasedReleaseTextRender = (shouldShowBlackoutText: boolean, androidStagedRolloutNotStarted: boolean) => {
        if (androidStagedRolloutNotStarted) {
            const currentPhasedReleasePercent =
                typeof props.currentPercent === 'number' && !Number.isNaN(props.currentPercent) ?
                    props.currentPercent
                :   props.daysToPercent[props.phasedReleaseDay - 1];

            return (
                <Text style={textStyles}>
                    Your automated rollout sequence has not started; staged rollout percentage will remain at{' '}
                    {currentPhasedReleasePercent}% until you start the rollout sequence.
                </Text>
            );
        }

        if (shouldShowBlackoutText) {
            return (
                <Text style={textStyles}>
                    Your {dayCount}-day {phasedReleaseText} has been{' '}
                    <Text strong style={{ color: antGrey.grey7 }}>
                        Paused
                    </Text>{' '}
                    due to a scheduled blackout period.{' '}
                    {props.phasedRelease.blackoutEndsAt ?
                        `It will resume ${formatResumedAtString(props.phasedRelease.blackoutEndsAt, props.phasedRelease.autoStagedRolloutCustomHour)}`
                    :   ''}
                </Text>
            );
        }

        return (
            <Text style={textStyles}>
                {props.phasedRelease?.isSuperseded == true ?
                    'Superseded on '
                : props.isPhasedReleasePaused ?
                    `${phasedDisplayText} on `
                :   'Today is '}
                <Text strong style={{ color: antGrey.grey7 }}>
                    Day {props.phasedReleaseDay}
                </Text>{' '}
                of a {dayCount}-day {phasedReleaseText}
            </Text>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', maxWidth: props.maxWidth }}>
            {phasedReleaseTextRender(shouldShowBlackoutText, props.androidStagedRolloutNotStarted)}
        </div>
    );
};

export default PhasedReleaseDayText;
